<template>
  <layout width="428">
    <template slot="header">
      {{ isNew ? 'Add new' : 'Edit' }} schedule
    </template>
    <validation-observer ref="form">
      <v-form ref="form" class="mx-4" :disabled="isLoading">
        <validation-provider v-slot="{ errors }" name="Name" rules="required">
          <v-text-field
            data-test-type="deviceName"
            class="mt-2 mb-2"
            v-model.trim="name"
            label="Name"
            clearable
            :error-messages="errors"
          ></v-text-field>
        </validation-provider>
        <div class="d-flex align-center mx-0" data-test="enabled">
          <div style="font-size: 16px">Enabled</div>
          <v-spacer />
          <v-switch
            data-test-switch="enabled"
            class="ma-0 py-3"
            hide-details
            :false-value="false"
            :true-value="true"
            v-model="enabled"
          ></v-switch>
        </div>
        <subheader>Schedule</subheader>
        <div class="d-flex align-center mx-0" data-test="useCron">
          <div style="font-size: 16px">Use cron format</div>
          <v-spacer />
          <v-switch
            disabled
            data-test-switch="enabled"
            class="ma-0 py-3"
            hide-details
            :false-value="false"
            :true-value="true"
            v-model="isRawCron"
          ></v-switch>
        </div>
        <validation-provider v-slot="{ errors }" name="Value" rules="required">
          <v-text-field
            data-test-type="cronValue"
            class="mt-2 mb-2"
            v-model="value"
            label="Value *"
            clearable
            :error-messages="errors"
          ></v-text-field>
        </validation-provider>
        <div>
          Specify values in order:
          <br />
          [min] [hour] [day] [month] [day of week]
          <br />
          <br />
          For example, to run every day 2am sharp: 0 2 * * *
        </div>
        <subheader>Notifications</subheader>
        <v-combobox
          v-model="tags"
          multiple
          label="Tags"
          append-icon
          clearable
          chips
          class="ma-0"
        >
          <template v-slot:selection="data">
            <v-chip color="primary" small>
              {{ data.item }}
            </v-chip>
          </template>
        </v-combobox>

        <v-textarea
          label="Message"
          data-test="Message"
          v-model="message"
          no-resize
          rows="1"
          auto-grow
        ></v-textarea>
        <subheader>Description</subheader>
        <v-textarea
          data-test="description"
          v-model="description"
          no-resize
          rows="1"
          auto-grow
        ></v-textarea>
      </v-form>
    </validation-observer>
    <v-divider />
    <template #footer>
      <v-spacer />
      <v-btn text @click="close" data-test-action="deviceCloseAction">
        cancel
      </v-btn>
      <v-btn
        color="primary"
        data-test-action="deviceCreateOrEditAction"
        :loading="isLoading"
        text
        @click.stop="handleSave"
      >
        <template v-if="isNew">add</template>
        <template v-else>save</template>
      </v-btn>
    </template>
  </layout>
</template>

<script>
import Layout from '@/layouts/LayoutPopup';
import {
  computed,
  ref,
  onMounted,
  reactive,
  toRefs
} from '@vue/composition-api';

setInteractionMode('eager');

import { required } from 'vee-validate/dist/rules';
import {
  extend,
  ValidationObserver,
  ValidationProvider,
  setInteractionMode
} from 'vee-validate';

extend('required', {
  ...required,
  message: '{_field_} can not be empty'
});

import useAuth from '@/api/auth';
import { useLazyQuery, useMutation, useResult } from '@vue/apollo-composable';

import schedules from '@/api/schedules/list.graphql';
import objectItem from '@/api/objects/item.graphql';
import scheduleItem from '@/api/schedules/item.graphql';

export default {
  name: 'CreateSchedule',
  props: {
    entity: {
      type: [Object],
      required: true
    },
    isCollections: {
      type: Boolean,
      default: false
    },
    entityType: {
      type: String,
      default: ''
    },
    linkAfterCreation: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    callbackAfter: {
      type: Function,
      default: () => {}
    }
  },
  components: {
    Layout,
    ValidationObserver,
    ValidationProvider
  },

  setup(props, { emit, root, refs }) {
    const controller = new window.AbortController();
    const { user, profile } = new useAuth();
    const localEntity = reactive({
      id: props.entity.id,
      schemaId: props.entity.schemaId,
      name: props.entity.name,
      enabled: true,
      isRawCron: true,
      description: props.entity.description,
      value: '',
      tags: [],
      message: ''
    });

    const { mutate: linkObjToObj } = useMutation(
      objectItem.connectObjectsToObject
    );

    const { mutate: createObject, loading: isCreatingObject } = useMutation(
      scheduleItem.createObjectWithProperties,
      {
        context: {
          fetchOptions: {
            signal: controller.signal
          }
        }
      }
    );

    const { mutate: updateObject, loading: isUpdatingObject } = useMutation(
      objectItem.update,
      {
        context: {
          fetchOptions: {
            signal: controller.signal
          }
        }
      }
    );

    const {
      mutate: updateProperties,
      loading: isUpdatingProperties
    } = useMutation(scheduleItem.updatePropertiesByKeys, {
      context: {
        fetchOptions: {
          signal: controller.signal
        }
      }
    });

    const lazyQuery = useLazyQuery(schedules.loadSchemaSchedule, null, {
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'network-only'
    });

    const schemaSchedule = useResult(lazyQuery.result, [], data => {
      return data.schemata?.[0];
    });

    const isNew = computed(() => {
      return !localEntity.id;
    });

    function close() {
      if (isCreatingObject.value || isUpdatingObject.value) {
        controller.abort();
      }

      emit('close');
    }

    const createObjectHandler = () => {
      createObject({
        input: {
          schemaId: schemaSchedule.value.id,
          name: localEntity.name,
          enabled: localEntity.enabled,
          description: localEntity.description,
          editorGroup: user.value.defaultEditorgroup,
          keyedProperties: [
            {
              propertyKey: 'scheduleValue',
              value: localEntity.value
            },
            {
              propertyKey: 'settingsMessage',
              value: localEntity.message
            },
            {
              propertyKey: 'settingsTags',
              value: localEntity.tags
            }
          ]
        }
      }).then(({ data }) => {
        close();
        props.callbackAfter();

        if (props.linkAfterCreation) {
          linkObjToObj({
            input: {
              objectsToObject: {
                object1Id: root.$route.query?.nestedId || root.$route.params.id,
                object2Id: data.createObject.object.id
              }
            }
          }).then(() => {
            close();
          });
        } else {
          redirectToCard(data.createObjectWithProperties.uuid);
        }
      });
    };

    const updateObjectHandler = () => {
      updateObject({
        input: {
          id: props.entity.id,
          patch: {
            name: localEntity.name,
            description: localEntity.description,
            enabled: localEntity.enabled
          }
        }
      })
        .then(() => {
          return updateProperties({
            input: {
              objectId: props.entity.id,
              propertiesArray: [
                {
                  propertyKey: 'scheduleValue',
                  value: localEntity.value
                },
                {
                  propertyKey: 'settingsMessage',
                  value: localEntity.message
                },
                {
                  propertyKey: 'settingsTags',
                  value: localEntity.tags
                }
              ]
            }
          });
        })
        .then(() => {
          close();
          props.callbackAfter();
        });
    };

    const redirectToCard = objectId => {
      let routeName = 'schedulesTableComponent';

      root.$router.push({
        name: routeName,
        params: { id: objectId }
      });
    };

    const getPropByKey = key => {
      return props.entity.objectProperties.find(item => item.key === key);
    };

    onMounted(() => {
      if (isNew.value) {
        lazyQuery.load();
        localEntity.enabled = true;
      } else {
        localEntity.enabled = props.entity.enabled;
        localEntity.tags = getPropByKey('settingsTags').value;
        localEntity.message = getPropByKey('settingsMessage').value;
        localEntity.value = getPropByKey('scheduleValue').value;
      }
    });

    const handleSave = () => {
      refs.form.validate().then(success => {
        if (!success) {
          return;
        }

        if (isNew.value) {
          createObjectHandler();
        } else {
          updateObjectHandler();
        }
      });
    };

    return {
      ...toRefs(localEntity),
      isNew,
      close,
      handleSave,
      isLoading: isCreatingObject || isUpdatingObject,
      isLoadSchemas: lazyQuery.loading
    };
  }
};
</script>
